// adminportal/src/pages/AdminDashboard.tsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/Card';
import Button from '../components/Button';
import { apiUrl, transferMonitoringApiUrl, isApiUrlConfigured } from '../config';
import { RefreshCw, Wifi, Bell, AlertTriangle, XCircle, UserX } from 'lucide-react';
import SftpMonitoringChart from '../components/SftpMonitoringChart';
import ErrorBoundary from '../components/ErrorBoundary';

interface FailedLogin {
  userId: string;
  username: string;
  timestamp: string;
  ipAddress: string;
  reason?: string;
}

interface DashboardData {
  activeUsers: number;
  recentLogins: Array<{
    userId: string;
    timestamp: string;
    status: string;
    ipAddress: string;
  }>;
  failedLogins?: FailedLogin[];
  filesProcessed: {
    total: number;
    uploads: number;
    downloads: number;
  };
  errorCounts: {
    failedLogins: number;
    incompleteTransfers: number;
    s3Errors: number;
    apiErrors: number;
    lastUpdated: string;
  };
  sftpMetrics?: {
    serverId: string;
    period: string;
    lastUpdated: string;
    note?: string;
    metrics: {
      BytesIn: {
        description: string;
        unit: string;
        datapoints: Array<{
          timestamp: string;
          sum: number;
          average: number;
          maximum: number;
        }>;
        summary: {
          total: number;
          average: number;
          max: number;
        };
      };
      BytesOut: {
        description: string;
        unit: string;
        datapoints: Array<{
          timestamp: string;
          sum: number;
          average: number;
          maximum: number;
        }>;
        summary: {
          total: number;
          average: number;
          max: number;
        };
      };
      FilesIn: {
        description: string;
        unit: string;
        datapoints: Array<{
          timestamp: string;
          sum: number;
          average: number;
          maximum: number;
        }>;
        summary: {
          total: number;
          average: number;
          max: number;
        };
      };
      FilesOut: {
        description: string;
        unit: string;
        datapoints: Array<{
          timestamp: string;
          sum: number;
          average: number;
          maximum: number;
        }>;
        summary: {
          total: number;
          average: number;
          max: number;
        };
      };
    };
  };
  workflowStats?: {
    totalWorkflows: number;
    totalServers: number;
    serversWithWorkflows: number;
    workflowTypes: {
      copy: number;
      custom: number;
      tag: number;
      delete: number;
      decrypt: number;
      other: number;
    };
    totalExecutions: number;
    successfulExecutions: number;
    failedExecutions: number;
    executionRate: number;
    lastUpdated: string;
  };
  serverStatus?: {
    serverId: string;
    state: string;
    stateMessage: string;
    endpoint: string | null;
    endpointType: string | null;
    workflowDetails?: {
      OnUpload?: Array<{
        WorkflowId: string;
        ExecutionRole: string;
      }>;
    } | null;
    lastUpdated: string;
  };
  dataVolume?: {
    totalBytes: number;
    totalFiles: number;
  };
  systemStatus?: {
    database: string;
    s3: string;
    cognito: string;
    apiGateway: string;
    lastCheck: string;
  };
  quarantineData?: {
    totalFiles: number;
    totalSize: string;
    lastScan: string;
    infectedFiles: Array<{
      filename: string;
      size: string;
      detectedAt: string;
      threatType: string;
      originalPath: string;
    }>;
  };
  lastUpdated: string;
}

// Helper function to safely render values
const safeRender = (value: any): string => {
  if (value === null || value === undefined) return '0';
  if (typeof value === 'object') return '0';
  return String(value);
};

export default function AdminDashboard() {
  console.log('🎯 AdminDashboard component rendering');
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'statistics' | 'monitoring'>('overview');
  const [notifications, setNotifications] = useState<
    Array<{ id: string; message: string; type: 'error' | 'warning' | 'info'; timestamp: Date }>
  >([]);
  const intervalRef = useRef<number | null>(null);

  const addNotification = useCallback((message: string, type: 'error' | 'warning' | 'info') => {
    const notification = {
      id: Date.now().toString(),
      message,
      type,
      timestamp: new Date(),
    };

    setNotifications(prev => [notification, ...prev.slice(0, 9)]); // Keep last 10 notifications

    // Auto-remove after 10 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 10000);
  }, []);

  const fetchDashboardData = useCallback(
    async (manual = false) => {
      console.log('🚀 fetchDashboardData called, manual:', manual);
      try {
        if (manual) {
          setLoading(true);
          addNotification('Manually refreshing dashboard data...', 'info');
        }

        // Check if API URL is configured
        if (!isApiUrlConfigured()) {
          console.warn('⚠️ API URL not configured, skipping fetch');
          setLoading(false);
          return;
        }

        // Fetch data from available APIs
        const [dashboardResponse, usersResponse, costResponse] = await Promise.allSettled([
          fetch(`${apiUrl('dashboard-data')}?t=${Date.now()}`), // Cache busting
          fetch(apiUrl('users')),
          fetch(apiUrl('cost-breakdown?period=daily')),
        ]);

        // Handle dashboard data
        let dashboardData: any = {};
        if (
          dashboardResponse.status === 'fulfilled' &&
          dashboardResponse.value &&
          dashboardResponse.value.ok
        ) {
          dashboardData = await (dashboardResponse.value as Response).json();
          console.log('📊 Dashboard data received:', dashboardData);
          console.log('📊 Dashboard data details:', {
            activeUsers: dashboardData?.activeUsers,
            filesProcessed: dashboardData?.filesProcessed,
            dataVolume: dashboardData?.dataVolume,
            errorCounts: dashboardData?.errorCounts,
            systemStatus: dashboardData?.systemStatus,
            sftpMetrics: dashboardData?.sftpMetrics,
            workflowStats: dashboardData?.workflowStats,
            serverStatus: dashboardData?.serverStatus,
            quarantineData: dashboardData?.quarantineData,
          });
        } else {
          console.error('❌ Dashboard API failed:', dashboardResponse);
        }

        // Handle users data
        let activeUsers = 0;
        if (usersResponse.status === 'fulfilled' && usersResponse.value && usersResponse.value.ok) {
          const usersData = await (usersResponse.value as Response).json();
          activeUsers = usersData.users?.length || 0;
        }

        // Handle cost data
        let costData: any = {};
        if (costResponse.status === 'fulfilled' && costResponse.value && costResponse.value.ok) {
          costData = await (costResponse.value as Response).json();
        }

        // Use real data from APIs or fallback to empty data
        const transformedData: DashboardData = {
          activeUsers,
          recentLogins: dashboardData?.recentLogins || [],
          filesProcessed: (() => {
            const fp = dashboardData?.filesProcessed;
            if (!fp || typeof fp !== 'object' || Array.isArray(fp)) {
              return { total: 0, uploads: 0, downloads: 0 };
            }
            // Ensure we have the expected properties and they are numbers
            return {
              total: typeof fp.total === 'number' ? fp.total : 0,
              uploads: typeof fp.uploads === 'number' ? fp.uploads : 0,
              downloads: typeof fp.downloads === 'number' ? fp.downloads : 0,
            };
          })(),
          errorCounts: dashboardData?.errorCounts || {
            failedLogins: 0,
            incompleteTransfers: 0,
            s3Errors: 0,
            apiErrors: 0,
            lastUpdated: new Date().toISOString(),
          },
          sftpMetrics: dashboardData?.sftpMetrics,
          workflowStats: dashboardData?.workflowStats,
          serverStatus: dashboardData?.serverStatus,
          dataVolume: dashboardData?.dataVolume || { totalBytes: 0, totalFiles: 0 },
          systemStatus: dashboardData?.systemStatus || {
            database: 'unknown',
            s3: 'unknown',
            cognito: 'unknown',
            apiGateway: 'unknown',
            lastCheck: new Date().toISOString(),
          },
          quarantineData: dashboardData?.quarantineData || {
            totalFiles: 0,
            totalSize: '0 Bytes',
            lastScan: new Date().toISOString(),
            infectedFiles: [],
          },
          lastUpdated: dashboardData?.lastUpdated || new Date().toISOString(),
        };

        // Dashboard data transformed successfully
        setDashboardData(transformedData);
        setError(null);
        setLastRefresh(new Date());

        if (manual) {
          addNotification('Dashboard data refreshed successfully', 'info');
        }
      } catch (err: unknown) {
        // Error fetching dashboard data
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        addNotification(`Failed to fetch dashboard data: ${errorMessage}`, 'error');
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  useEffect(() => {
    console.log('🎯 AdminDashboard useEffect triggered');
    // Add a small delay to let the page fully load before making API calls
    const timer = window.setTimeout(() => {
      console.log('⏰ Timer triggered, calling fetchDashboardData');
      fetchDashboardData();
    }, 100) as number;

    // Polling every 5 minutes for real-time updates (reduced frequency for better performance)
    intervalRef.current = setInterval(fetchDashboardData, 300000) as unknown as number;

    return () => {
      window.clearTimeout(timer);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchDashboardData]);

  const handleManualRefresh = useCallback(() => {
    fetchDashboardData(true);
  }, [fetchDashboardData]);

  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        addNotification('Push notifications enabled for critical events', 'info');
      }
    }
  }, [addNotification]);

  const formatTimestamp = useCallback((timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  }, []);

  // Helper function to format bytes
  const formatBytes = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }, []);

  // Memoized calculations for expensive operations
  const memoizedDashboardStats = useMemo(() => {
    if (!dashboardData) return null;

    return {
      totalErrors:
        (dashboardData.errorCounts?.failedLogins || 0) +
        (dashboardData.errorCounts?.incompleteTransfers || 0) +
        (dashboardData.errorCounts?.s3Errors || 0) +
        (dashboardData.errorCounts?.apiErrors || 0),
      systemHealth: dashboardData.systemStatus
        ? (Object.values(dashboardData.systemStatus).filter(status => status === 'healthy').length /
            Object.keys(dashboardData.systemStatus).length) *
          100
        : 0,
      dataVolumeFormatted: dashboardData.dataVolume
        ? formatBytes(dashboardData.dataVolume.totalBytes || 0)
        : '0 Bytes',
      quarantineCount: dashboardData.quarantineData?.totalFiles || 0,
    };
  }, [dashboardData]);

  const memoizedRecentLogins = useMemo(() => {
    if (!dashboardData?.recentLogins) return [];
    return dashboardData.recentLogins.slice(0, 5); // Show only last 5
  }, [dashboardData?.recentLogins]);

  const memoizedNotifications = useMemo(() => {
    return notifications.slice(0, 5); // Show only last 5 notifications
  }, [notifications]);

  if (loading && !dashboardData) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-slate-400'>Loading dashboard data...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className='space-y-4'>
        {/* Connection Status and Controls */}
        <div className='flex items-center justify-between bg-slate-800/50 rounded-lg p-4 border border-slate-700'>
          <div className='flex items-center gap-4'>
            <div className='flex items-center gap-2'>
              <Wifi className='w-5 h-5 text-green-400' />
              <span className='text-sm text-slate-300'>Transfer Monitoring API (60s refresh)</span>
            </div>

            {lastRefresh && (
              <span className='text-xs text-slate-400'>
                Last updated: {formatTimestamp(lastRefresh.toISOString())}
              </span>
            )}
          </div>

          <div className='flex items-center gap-2'>
            <Button
              onClick={requestNotificationPermission}
              className='bg-blue-600 hover:bg-blue-700 text-white'
              variant='soft'
            >
              <Bell className='w-4 h-4 mr-2' />
              Enable Notifications
            </Button>

            <Button
              onClick={handleManualRefresh}
              disabled={loading}
              className='bg-indigo-600 hover:bg-indigo-700 text-white'
              variant='soft'
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Notifications */}
        {notifications.length > 0 && (
          <div className='space-y-2'>
            {notifications.map(notification => (
              <div
                key={notification.id}
                className={`p-3 rounded-lg border ${
                  notification.type === 'error'
                    ? 'bg-red-900/20 border-red-500/50 text-red-200'
                    : notification.type === 'warning'
                      ? 'bg-yellow-900/20 border-yellow-500/50 text-yellow-200'
                      : 'bg-blue-900/20 border-blue-500/50 text-blue-200'
                }`}
              >
                <div className='flex items-center gap-2'>
                  {notification.type === 'error' && <AlertTriangle className='w-4 h-4' />}
                  <span className='text-sm'>{notification.message}</span>
                  <span className='text-xs opacity-75 ml-auto'>
                    {formatTimestamp(notification.timestamp.toISOString())}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tab Navigation */}
        <div className='flex border-b border-white/10 mb-6'>
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'overview'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('statistics')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'statistics'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            Statistics
          </button>
          <button
            onClick={() => setActiveTab('monitoring')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'monitoring'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            Monitoring
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className='grid grid-cols-12 gap-4'>
            {/* Main Page Title */}
            <div className='col-span-12'>
              <div className='mb-6'>
                <h1 className='text-3xl font-bold text-white'>NGHS Transfer Portal</h1>
                <p className='text-slate-400 mt-2'>Real-time system overview and statistics</p>
              </div>
            </div>

            <div className='col-span-12'>
              <Card title='System Overview' subtitle='Key metrics and performance indicators'>
                <div className='grid grid-cols-12 gap-3'>
                  <div className='col-span-12 sm:col-span-3'>
                    <div className='rounded-xl border border-white/10 bg-white/5 p-4'>
                      <div className='text-2xl font-extrabold'>
                        {dashboardData?.activeUsers || 0}
                      </div>
                      <div className='text-slate-300'>Active Users</div>
                      <div className='text-xs text-slate-500 mt-1'>Total registered</div>
                    </div>
                  </div>
                  <div className='col-span-12 sm:col-span-3'>
                    <div className='rounded-xl border border-white/10 bg-white/5 p-4'>
                      <div className='text-2xl font-extrabold'>
                        {safeRender(dashboardData?.filesProcessed?.total)}
                      </div>
                      <div className='text-slate-300'>Files Processed</div>
                      <div className='text-xs text-slate-400 mt-1'>
                        {safeRender(dashboardData?.filesProcessed?.uploads)} uploads,{' '}
                        {safeRender(dashboardData?.filesProcessed?.downloads)} downloads
                      </div>
                      <div className='text-xs text-slate-500 mt-1'>Last 24 hours</div>
                    </div>
                  </div>
                  <div className='col-span-12 sm:col-span-3'>
                    <div className='rounded-xl border border-white/10 bg-white/5 p-4'>
                      <div className='text-2xl font-extrabold'>
                        {(dashboardData?.errorCounts?.failedLogins || 0) +
                          (dashboardData?.errorCounts?.apiErrors || 0)}
                      </div>
                      <div className='text-slate-300'>Errors</div>
                      <div className='text-xs text-slate-500 mt-1'>Last 7 days</div>
                    </div>
                  </div>
                  <div className='col-span-12 sm:col-span-3'>
                    <div className='rounded-xl border border-white/10 bg-white/5 p-4'>
                      <div className='text-sm text-slate-400'>Last Updated</div>
                      <div className='text-slate-300'>
                        {dashboardData?.lastUpdated
                          ? formatTimestamp(dashboardData.lastUpdated)
                          : 'Unknown'}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Recent Login Activity */}
            <div className='col-span-12 lg:col-span-6'>
              <Card title='Recent Login Activity' subtitle='Last 30 user logins (showing 4)'>
                {dashboardData?.recentLogins && dashboardData.recentLogins.length > 0 ? (
                  <div className='max-h-48 overflow-y-auto space-y-2 pr-2 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800'>
                    {dashboardData?.recentLogins.slice(0, 4).map((login, index) => (
                      <div
                        key={index}
                        className='flex items-center justify-between p-2 rounded-lg bg-white/5'
                      >
                        <div>
                          <div className='font-medium text-slate-200'>{login.userId}</div>
                          <div className='text-sm text-slate-400'>{login.ipAddress}</div>
                        </div>
                        <div className='text-right'>
                          <div
                            className={`text-xs px-2 py-1 rounded ${
                              login.status === 'success'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-rose-500/20 text-rose-400'
                            }`}
                          >
                            {login.status}
                          </div>
                          <div className='text-xs text-slate-400 mt-1'>
                            {formatTimestamp(login.timestamp)}
                          </div>
                        </div>
                      </div>
                    ))}
                    {dashboardData?.recentLogins.length > 4 && (
                      <div className='text-xs text-slate-400 text-center py-2 border-t border-white/10'>
                        Scroll to see {dashboardData?.recentLogins.length - 4} more logins
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='text-slate-400 text-center py-4'>No recent login activity</div>
                )}
              </Card>
            </div>

            {/* Server Status with Workflows */}
            <div className='col-span-12'>
              <Card
                title='Server Details'
                subtitle='SFTP server configuration and attached workflows'
              >
                {dashboardData?.serverStatus &&
                dashboardData.serverStatus.serverId !== 'not-configured' &&
                dashboardData.serverStatus.state !== 'not-configured' &&
                dashboardData.serverStatus.serverId !== 'error' ? (
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                    <div className='space-y-4'>
                      <div className='p-4 bg-slate-800/50 rounded-lg'>
                        <h4 className='font-medium text-white mb-3'>Server Information</h4>
                        <div className='space-y-2 text-sm'>
                          <div className='flex justify-between'>
                            <span className='text-slate-400'>Server ID:</span>
                            <span className='text-white font-mono'>
                              {dashboardData?.serverStatus?.serverId}
                            </span>
                          </div>
                          <div className='flex justify-between'>
                            <span className='text-slate-400'>Status:</span>
                            <span
                              className={`font-medium ${
                                dashboardData?.serverStatus?.state === 'ONLINE'
                                  ? 'text-green-400'
                                  : 'text-red-400'
                              }`}
                            >
                              {dashboardData?.serverStatus?.state}
                            </span>
                          </div>
                          <div className='flex justify-between'>
                            <span className='text-slate-400'>Endpoint Type:</span>
                            <span className='text-white'>
                              {dashboardData?.serverStatus?.endpointType || 'N/A'}
                            </span>
                          </div>
                          <div className='flex justify-between'>
                            <span className='text-slate-400'>Endpoint:</span>
                            {dashboardData?.serverStatus?.endpoint ? (
                              <a
                                href={`sftp://${dashboardData.serverStatus.endpoint}`}
                                className='text-blue-400 hover:text-blue-300 font-mono text-xs underline'
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                {dashboardData.serverStatus.endpoint}
                              </a>
                            ) : (
                              <span className='text-white font-mono text-xs'>N/A</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='space-y-4'>
                      <div className='p-4 bg-slate-800/50 rounded-lg'>
                        <h4 className='font-medium text-white mb-3'>Attached Workflows</h4>
                        {dashboardData?.serverStatus?.workflowDetails?.OnUpload &&
                        dashboardData?.serverStatus?.workflowDetails?.OnUpload.length > 0 ? (
                          <div className='space-y-2'>
                            {dashboardData?.serverStatus?.workflowDetails?.OnUpload.map(
                              (workflow, index) => (
                                <div
                                  key={index}
                                  className='flex items-center justify-between bg-slate-700/50 rounded p-3'
                                >
                                  <div>
                                    <div className='text-sm font-medium text-white'>
                                      {workflow.WorkflowId}
                                    </div>
                                    <div className='text-xs text-slate-400'>
                                      Execution Role: {workflow.ExecutionRole}
                                    </div>
                                  </div>
                                  <div className='w-2 h-2 bg-green-400 rounded-full'></div>
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <div className='text-center py-4'>
                            <div className='text-slate-400 text-sm'>No workflows attached</div>
                            <div className='text-slate-500 text-xs mt-1'>
                              Go to Workflows page to attach workflows to this server
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='text-center py-8'>
                    <div className='text-4xl mb-4'>🔧</div>
                    <div className='text-slate-300 text-lg mb-2'>SFTP Server Not Configured</div>
                    <div className='text-slate-400 text-sm mb-4'>
                      The SFTP server needs to be set up before server details can be displayed.
                    </div>
                    <div className='text-slate-500 text-xs'>
                      Server configuration will appear here once the transfer server is deployed.
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* Navigation Card */}
            <div className='col-span-12'>
              <Card title='Navigation'>
                <div className='grid grid-cols-12 gap-3'>
                  <div className='col-span-12 sm:col-span-3'>
                    <Link to='/admin/users/create'>
                      <Button className='w-full justify-center'>➕ Create New User</Button>
                    </Link>
                  </div>
                  <div className='col-span-12 sm:col-span-3'>
                    <Link to='/admin/users'>
                      <Button variant='soft' className='w-full justify-center'>
                        👥 Manage Users
                      </Button>
                    </Link>
                  </div>
                  <div className='col-span-12 sm:col-span-3'>
                    <Link to='/admin/companies'>
                      <Button variant='soft' className='w-full justify-center'>
                        🏢 Manage Companies
                      </Button>
                    </Link>
                  </div>
                  <div className='col-span-12 sm:col-span-3'>
                    <Link to='/admin/sftp-connectors'>
                      <Button variant='soft' className='w-full justify-center'>
                        📊 SFTP Connectors
                      </Button>
                    </Link>
                  </div>
                  <div className='col-span-12 sm:col-span-3'>
                    <Link to='/admin/settings'>
                      <Button variant='soft' className='w-full justify-center'>
                        ⚙️ Settings
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            </div>

            {error && (
              <div className='col-span-12'>
                <Card title='Error' subtitle='Failed to load dashboard data'>
                  <div className='text-rose-400 mb-3'>{error}</div>
                  <Button onClick={() => fetchDashboardData(true)} variant='soft'>
                    Retry
                  </Button>
                </Card>
              </div>
            )}
          </div>
        )}

        {activeTab === 'statistics' && (
          <div className='space-y-6'>
            <h2 className='text-2xl font-bold text-white'>Statistics</h2>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
              <Card title='User Statistics' subtitle='User activity and engagement'>
                <div className='space-y-4'>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Total Users</span>
                    <span className='text-2xl font-bold text-blue-400'>
                      {dashboardData?.activeUsers || 0}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Active Users</span>
                    <span className='text-2xl font-bold text-green-400'>
                      {dashboardData?.activeUsers || 0}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>New Users (30 days)</span>
                    <span className='text-2xl font-bold text-purple-400'>
                      {dashboardData?.activeUsers || 0}
                    </span>
                  </div>
                </div>
              </Card>

              <Card title='Transfer Statistics' subtitle='File transfer metrics'>
                <div className='space-y-4'>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Files Transferred</span>
                    <span className='text-2xl font-bold text-blue-400'>
                      {String((dashboardData as any)?.filesProcessed?.total || 0)}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Data Transferred</span>
                    <span className='text-2xl font-bold text-green-400'>
                      {(
                        ((dashboardData as any)?.dataVolume?.totalBytes || 0) /
                        (1024 * 1024 * 1024)
                      ).toFixed(2)}{' '}
                      GB
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Success Rate</span>
                    <span className='text-2xl font-bold text-purple-400'>
                      {String((dashboardData as any)?.workflowStats?.successRate || 0)}%
                    </span>
                  </div>
                </div>
              </Card>

              <Card
                title='Workflow Execution Metrics'
                subtitle='Success and failure rates (Last 24 hours)'
              >
                <div className='space-y-4'>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Total Executions</span>
                    <span className='text-2xl font-bold text-blue-400'>
                      {dashboardData?.workflowStats?.totalExecutions || 0}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Successful</span>
                    <span className='text-2xl font-bold text-green-400'>
                      {dashboardData?.workflowStats?.successfulExecutions || 0}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Failed</span>
                    <span className='text-2xl font-bold text-red-400'>
                      {dashboardData?.workflowStats?.failedExecutions || 0}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Success Rate</span>
                    <span className='text-2xl font-bold text-purple-400'>
                      {dashboardData?.workflowStats?.executionRate || 0}%
                    </span>
                  </div>
                </div>
              </Card>
            </div>

            {/* SFTP Monitoring Charts */}
            <div className='mt-8'>
              <h3 className='text-xl font-semibold text-white mb-6'>SFTP Transfer Activity</h3>
              {dashboardData?.sftpMetrics ? (
                <SftpMonitoringChart sftpMetrics={dashboardData?.sftpMetrics} />
              ) : (
                <Card className='p-6 border-slate-700'>
                  <div className='text-center text-slate-400'>
                    <div className='text-4xl mb-2'>📊</div>
                    <div>No SFTP metrics available</div>
                  </div>
                </Card>
              )}
            </div>
          </div>
        )}

        {activeTab === 'monitoring' && (
          <div className='space-y-6'>
            <h2 className='text-2xl font-bold text-white'>Monitoring</h2>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Card title='System Status' subtitle='Real-time system health'>
                <div className='space-y-4'>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Database</span>
                    <span
                      className={`text-lg font-bold ${(dashboardData as any)?.systemStatus?.database === 'healthy' ? 'text-green-400' : 'text-red-400'}`}
                    >
                      {(dashboardData as any)?.systemStatus?.database || 'unknown'}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>S3 Storage</span>
                    <span
                      className={`text-lg font-bold ${(dashboardData as any)?.systemStatus?.s3 === 'healthy' ? 'text-green-400' : 'text-red-400'}`}
                    >
                      {(dashboardData as any)?.systemStatus?.s3 || 'unknown'}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Cognito Auth</span>
                    <span
                      className={`text-lg font-bold ${(dashboardData as any)?.systemStatus?.cognito === 'healthy' ? 'text-green-400' : 'text-red-400'}`}
                    >
                      {(dashboardData as any)?.systemStatus?.cognito || 'unknown'}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>API Gateway</span>
                    <span
                      className={`text-lg font-bold ${(dashboardData as any)?.systemStatus?.apiGateway === 'healthy' ? 'text-green-400' : 'text-red-400'}`}
                    >
                      {(dashboardData as any)?.systemStatus?.apiGateway || 'unknown'}
                    </span>
                  </div>
                  <div className='text-xs text-slate-500 mt-2'>
                    Last check:{' '}
                    {(dashboardData as any)?.systemStatus?.lastCheck
                      ? new Date((dashboardData as any).systemStatus.lastCheck).toLocaleString()
                      : 'Never'}
                  </div>
                </div>
              </Card>

              <Card title='Failed Login Attempts' subtitle='Recent authentication failures'>
                {dashboardData?.failedLogins && dashboardData.failedLogins.length > 0 ? (
                  <div className='space-y-2 max-h-96 overflow-y-auto'>
                    {dashboardData.failedLogins.slice(0, 10).map((login, index) => (
                      <div
                        key={index}
                        className='p-2 rounded border bg-red-500/10 border-red-500/20'
                      >
                        <div className='flex items-center justify-between'>
                          <div className='flex items-center gap-2 flex-1 min-w-0'>
                            <UserX className='w-4 h-4 text-red-400 flex-shrink-0' />
                            <div className='min-w-0 flex-1'>
                              <div className='font-medium text-white truncate'>
                                {login.username || login.userId || 'Unknown User'}
                              </div>
                              {login.reason && (
                                <div className='text-xs text-red-300 truncate'>{login.reason}</div>
                              )}
                            </div>
                          </div>
                          <div className='text-right flex-shrink-0 ml-2'>
                            <div className='text-xs text-slate-400'>
                              {new Date(login.timestamp).toLocaleString()}
                            </div>
                            {login.ipAddress && login.ipAddress !== 'unknown' && (
                              <div className='text-xs text-slate-500'>{login.ipAddress}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {dashboardData.failedLogins.length > 10 && (
                      <div className='text-center text-xs text-slate-400 pt-2'>
                        Showing 10 of {dashboardData.failedLogins.length} failed attempts
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='text-center py-4 text-slate-400'>
                    <XCircle className='w-8 h-8 mx-auto mb-2 opacity-50' />
                    <div>No failed login attempts</div>
                  </div>
                )}
              </Card>

              <Card title='Error Monitoring' subtitle='System errors and alerts'>
                <div className='space-y-4'>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Failed Logins</span>
                    <span className='text-2xl font-bold text-red-400'>
                      {dashboardData?.errorCounts?.failedLogins || 0}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>API Errors</span>
                    <span className='text-2xl font-bold text-orange-400'>
                      {dashboardData?.errorCounts?.apiErrors || 0}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>S3 Errors</span>
                    <span className='text-2xl font-bold text-yellow-400'>
                      {dashboardData?.errorCounts?.s3Errors || 0}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Incomplete Transfers</span>
                    <span className='text-2xl font-bold text-purple-400'>
                      {dashboardData?.errorCounts?.incompleteTransfers || 0}
                    </span>
                  </div>
                </div>
              </Card>

              <Card title='Performance Metrics' subtitle='System performance indicators'>
                <div className='space-y-4'>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Active Users</span>
                    <span className='text-2xl font-bold text-blue-400'>
                      {dashboardData?.activeUsers || 0}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Quarantine Files (7 days)</span>
                    <span className='text-2xl font-bold text-red-400'>
                      {(dashboardData as any)?.quarantineData?.totalFiles || 0}
                    </span>
                  </div>
                  {(dashboardData as any)?.quarantineData?.totalFiles > 0 && (
                    <div className='mt-2 p-3 bg-red-900/20 border border-red-700/50 rounded-lg'>
                      <div className='text-sm text-red-300 mb-2'>
                        🚨 Quarantined Files by Company:
                      </div>
                      {Object.entries((dashboardData as any)?.quarantineData?.byCompany || {}).map(
                        ([company, data]: [string, any]) => (
                          <div key={company} className='text-xs text-slate-300 mb-1'>
                            <span className='font-medium'>{company}:</span> {data.count} files
                            {data.files.length > 0 && (
                              <div className='ml-2 text-slate-400'>
                                {data.files.slice(0, 3).map((file: any, idx: number) => (
                                  <div key={idx}>
                                    • {file.filename} {file.user && `(${file.user})`}
                                  </div>
                                ))}
                                {data.files.length > 3 && (
                                  <div>• ... and {data.files.length - 3} more</div>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  )}
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Files Processed</span>
                    <span className='text-2xl font-bold text-green-400'>
                      {safeRender(dashboardData?.filesProcessed?.total)}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Uploads</span>
                    <span className='text-lg font-bold text-blue-400'>
                      {safeRender(dashboardData?.filesProcessed?.uploads)}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Downloads</span>
                    <span className='text-lg font-bold text-purple-400'>
                      {safeRender(dashboardData?.filesProcessed?.downloads)}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Data Volume</span>
                    <span className='text-2xl font-bold text-purple-400'>
                      {(() => {
                        const bytes = (dashboardData as any)?.dataVolume?.totalBytes || 0;
                        if (bytes >= 1024 * 1024 * 1024) {
                          return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
                        } else if (bytes >= 1024 * 1024) {
                          return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
                        } else if (bytes >= 1024) {
                          return `${(bytes / 1024).toFixed(2)} KB`;
                        } else {
                          return `${bytes} bytes`;
                        }
                      })()}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-xs text-slate-500'>Total storage</span>
                    <span className='text-xs text-slate-500'>
                      {String((dashboardData as any)?.dataVolume?.totalFiles || 0)} files
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-slate-300'>Success Rate</span>
                    <span className='text-2xl font-bold text-emerald-400'>
                      {String((dashboardData as any)?.workflowStats?.successRate || 0)}%
                    </span>
                  </div>
                </div>
              </Card>

              <Card title='Recent Activity' subtitle='Latest system events'>
                <div className='space-y-3'>
                  {dashboardData?.recentLogins?.slice(0, 5).map((login, index) => (
                    <div
                      key={index}
                      className='flex justify-between items-center p-2 bg-slate-800/50 rounded'
                    >
                      <div>
                        <span className='text-slate-300 text-sm'>{login.userId}</span>
                        <span className='text-slate-500 text-xs ml-2'>{login.ipAddress}</span>
                      </div>
                      <div className='text-right'>
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            login.status === 'success'
                              ? 'bg-green-900/20 text-green-400'
                              : 'bg-red-900/20 text-red-400'
                          }`}
                        >
                          {login.status}
                        </span>
                        <div className='text-xs text-slate-500 mt-1'>
                          {new Date(login.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )) || (
                    <div className='text-slate-500 text-center py-4'>
                      No recent activity data available
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
